import React, { useEffect, useState } from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import truck from "./truck.png";

mapboxgl.accessToken =
  "pk.eyJ1IjoiY2hhcml0aC1qIiwiYSI6ImNsaXd6NTJ0YjAyd2Yzc3FmNjNyNmZqeGoifQ.mr-0WEzM4eIpcfq3JlTbVQ";

const MapComponent = ({ pathCoordinates, pastLocations = [] }) => {
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);

  // Convert pathCoordinates to array of [lon, lat] for Mapbox
  const coordinates = Array.isArray(pathCoordinates)
    ? pathCoordinates.map((coord) => [coord.lon, coord.lat])
    : [];

  // Convert pastLocations to array of [lon, lat] for Mapbox
  const pastCoordinates = Array.isArray(pastLocations)
    ? pastLocations.map((coord) => [coord.lon, coord.lat])
    : [];

  useEffect(() => {
    const mapInstance = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox://styles/mapbox/streets-v11',
      center:
        coordinates.length > 0 ? coordinates[coordinates.length - 1] : [80, 7], // Center on latest coordinate or default
      zoom: 8,
    });

    const draw = new MapboxDraw();
    mapInstance.addControl(draw);
    setMap(mapInstance);

    // Create custom marker with truck icon
    const lorryIcon = document.createElement('div');
    const img = document.createElement('img');
    img.src = truck;
    img.width = 14;
    img.height = 30;
    lorryIcon.appendChild(img);

    const newMarker = new mapboxgl.Marker({ element: lorryIcon, rotation: 0 })
      .setLngLat(
        coordinates.length > 0 ? coordinates[coordinates.length - 1] : [80, 7]
      )
      .addTo(mapInstance);
    setMarker(newMarker);

    // Add source and layer for the path when map loads
    mapInstance.on('load', () => {
      // Add past locations path (gray/secondary color)
      if (pastCoordinates.length > 0) {
        mapInstance.addSource('past-path', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: pastCoordinates,
            },
          },
        });

        mapInstance.addLayer({
          id: 'past-path-layer',
          type: 'line',
          source: 'past-path',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': '#808080',
            'line-width': 3,
            'line-opacity': 0.6,
          },
        });

        // Add markers for start and end of past track
        if (pastCoordinates.length > 0) {
          // Start marker
          new mapboxgl.Marker({ color: '#22c55e' })
            .setLngLat(pastCoordinates[0])
            .setPopup(new mapboxgl.Popup().setText('Start Point'))
            .addTo(mapInstance);

          // End marker of past track
          new mapboxgl.Marker({ color: '#ef4444' })
            .setLngLat(pastCoordinates[pastCoordinates.length - 1])
            .setPopup(new mapboxgl.Popup().setText('Last Known Position'))
            .addTo(mapInstance);
        }
      }

      // Add live path (blue color)
      mapInstance.addSource('vehicle-path', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: coordinates, // Use converted coordinates
          },
        },
      });

      mapInstance.addLayer({
        id: 'vehicle-path-layer',
        type: 'line',
        source: 'vehicle-path',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#0000FF',
          'line-width': 3,
        },
      });
    });

    return () => mapInstance.remove();
  }, []); // Empty dependency array for map initialization

  // Update marker and path when pathCoordinates change
  useEffect(() => {
    if (!map || !marker || coordinates.length === 0) return;

    // Update marker position
    marker.setLngLat(coordinates[coordinates.length - 1]);

    // Calculate bearing for marker rotation
    if (coordinates.length > 1) {
      const bearing = calculateBearing(
        coordinates[coordinates.length - 2],
        coordinates[coordinates.length - 1]
      );
      marker.setRotation(bearing);
    }

    // Update live path
    if (map.getSource('vehicle-path')) {
      map.getSource('vehicle-path').setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coordinates,
        },
      });
    }

    // Center map on the latest coordinate
    map.flyTo({
      center: coordinates[coordinates.length - 1],
      zoom: 8,
      essential: true,
    });
  }, [map, marker, pathCoordinates]); // Depend on pathCoordinates

  // Update past path when pastLocations change
  useEffect(() => {
    if (!map || pastCoordinates.length === 0) return;

    // Update past path if it exists
    if (map.getSource('past-path')) {
      map.getSource('past-path').setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: pastCoordinates,
        },
      });
    }

    // If no live coordinates, center on past track
    if (coordinates.length === 0 && pastCoordinates.length > 0) {
      map.flyTo({
        center: pastCoordinates[pastCoordinates.length - 1],
        zoom: 8,
        essential: true,
      });
    }
  }, [map, pastLocations]);

  // Function to calculate bearing between two points (in [lng, lat] format)
  const calculateBearing = (start, end) => {
    const startLng = start[0],
      startLat = start[1];
    const endLng = end[0],
      endLat = end[1];
    const dLon = (endLng - startLng) * (Math.PI / 180);
    const startLatRad = startLat * (Math.PI / 180);
    const endLatRad = endLat * (Math.PI / 180);

    const y = Math.sin(dLon) * Math.cos(endLatRad);
    const x =
      Math.cos(startLatRad) * Math.sin(endLatRad) -
      Math.sin(startLatRad) * Math.cos(endLatRad) * Math.cos(dLon);

    const initialBearing = Math.atan2(y, x);
    const bearingDeg = (initialBearing * (180 / Math.PI) + 360) % 360; // Normalize to 0-360

    return bearingDeg;
  };

  return (
    <div
      id="map"
      style={{ height: '500px', width: '100%' }}
    ></div>
  );
};

export default MapComponent;